import { useEffect, useRef, useState } from 'react';
import { ArrowUpTrayIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useProductImageUrls } from '../../../hooks/useBilling';
import { ALLOWED_IMAGE_TYPES, imageRejectionReason } from '../../../services/productImages';

const ACCEPT_ATTRIBUTE = ALLOWED_IMAGE_TYPES.join(',');

interface ProductImageFieldProps {
    /** The newly picked file, not uploaded until the form is submitted. */
    file: File | null;
    storedFileId: string | null;
    disabled?: boolean;
    onPick: (file: File | null) => void;
    onRemoveStored: () => void;
    /** Why a pick was refused - drops never reach the `accept` filter. */
    onReject: (reason: string) => void;
}

function useLocalPreview(file: File | null): string | null {
    const [url, setUrl] = useState<string | null>(null);

    useEffect(() => {
        if (!file) {
            setUrl(null);
            return;
        }
        const objectUrl = URL.createObjectURL(file);
        setUrl(objectUrl);
        return () => URL.revokeObjectURL(objectUrl);
    }, [file]);

    return url;
}

/** Drop-zone hero-image picker: a newly picked file and a stored one both
 *  show as a square preview row until removed. */
export function ProductImageField({
    file,
    storedFileId,
    disabled,
    onPick,
    onRemoveStored,
    onReject
}: ProductImageFieldProps) {
    const inputRef = useRef<HTMLInputElement>(null);
    const [isDragging, setIsDragging] = useState(false);
    const localPreview = useLocalPreview(file);
    const { imageUrls } = useProductImageUrls(file || !storedFileId ? [] : [storedFileId]);
    const storedPreview = storedFileId ? (imageUrls[storedFileId] ?? null) : null;

    const takeFirstImage = (files: FileList | null) => {
        const picked = files?.[0];
        // Cleared so re-picking the same file after a removal still fires onChange.
        if (inputRef.current) inputRef.current.value = '';
        if (!picked) return;
        const rejection = imageRejectionReason(picked);
        if (rejection) {
            onReject(rejection);
            return;
        }
        onPick(picked);
    };

    const hiddenInput = (
        <input
            ref={inputRef}
            type="file"
            accept={ACCEPT_ATTRIBUTE}
            onChange={(event) => takeFirstImage(event.target.files)}
            disabled={disabled}
            className="hidden"
        />
    );

    if (file || storedFileId) {
        const preview = localPreview ?? storedPreview;
        return (
            <div className="flex items-center gap-3 px-3.5 py-2.5 rounded-lg border border-gray-300 bg-white">
                {preview ? (
                    <img
                        src={preview}
                        alt=""
                        className="w-16 h-16 rounded-lg object-cover bg-gray-100 shrink-0"
                    />
                ) : (
                    <span className="w-16 h-16 rounded-lg bg-gray-100 shrink-0" />
                )}
                <div className="flex-1 min-w-0 flex flex-col">
                    <span className="text-[13px] font-semibold text-gray-800 truncate">
                        {file ? file.name : 'Current image'}
                    </span>
                    <span className="text-[11px] text-gray-400">
                        {file ? 'Uploads when you save' : 'Stored with this product'}
                    </span>
                </div>
                <button
                    type="button"
                    onClick={() => inputRef.current?.click()}
                    disabled={disabled}
                    className="shrink-0 text-[13px] font-semibold text-gray-500 hover:text-gray-800 transition-colors disabled:opacity-50"
                >
                    Replace
                </button>
                <button
                    type="button"
                    onClick={() => (file ? onPick(null) : onRemoveStored())}
                    disabled={disabled}
                    aria-label="Remove the product image"
                    className="shrink-0 text-gray-400 hover:text-gray-800 transition-colors disabled:opacity-50"
                >
                    <XMarkIcon className="w-5 h-5" />
                </button>
                {hiddenInput}
            </div>
        );
    }

    return (
        <>
            <button
                type="button"
                onClick={() => inputRef.current?.click()}
                disabled={disabled}
                onDragOver={(event) => {
                    event.preventDefault();
                    setIsDragging(true);
                }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={(event) => {
                    event.preventDefault();
                    setIsDragging(false);
                    takeFirstImage(event.dataTransfer.files);
                }}
                className={`flex flex-col items-center justify-center gap-1.5 px-4 py-5 w-full rounded-lg border-2 border-dashed transition-colors disabled:opacity-50 ${
                    isDragging
                        ? 'border-gray-400 bg-gray-100'
                        : 'border-gray-300 bg-gray-50 hover:bg-gray-100'
                }`}
            >
                <ArrowUpTrayIcon className="w-5 h-5 text-gray-400" />
                <span className="text-[13px] font-semibold text-gray-700">Choose an image</span>
                <span className="text-[11px] text-gray-400">
                    or drop it here - JPEG, PNG or WebP, up to 5MB
                </span>
            </button>
            {hiddenInput}
        </>
    );
}
